import { FC, useContext } from 'react';
import AsyncImage from '../AsyncImage/AsyncImage';
import PublisherLogo from '../PublisherLogo/PublisherLogo';
import FotoscapeLink from '../../../components/FotoscapeLink/FotoscapeLink';
import { StorySize } from '@/moment/types/story-types';
import './RecirculationCard.scss';
import { adjustFotoscapesLink } from '@/moment/net/softbox-types';
import { getIsLinkExternal } from '@components/FotoscapeLink/utils.ts';
import { useSearchParams } from 'react-router';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { useTranslation } from 'react-i18next';
import { getCurrentNumberOfInteractions } from '@moment/local-personalization/scorer.ts';

export interface RecirculationCardProps {
  uid?: string;
  title: string;
  category: string;
  publisherLogo?: string;
  publisherName?: string;
  darkPublisherLogo?: string;
  isDark?: boolean;
  image: string;
  link?: string;
  owner?: string;
}

const RecirculationCard: FC<RecirculationCardProps> = ({
  uid,
  title,
  category,
  publisherLogo,
  publisherName,
  darkPublisherLogo,
  isDark,
  image,
  link,
  owner,
}) => {
  const [searchParams] = useSearchParams();
  const analytics = useAnalytics();
  const context = useContext(CommonPageDataContext);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const cardContent = (
    <>
      <div
        role="group"
        key={uid}
        className="recirculation-card-image"
        data-testid="test-recirculation-card-image"
        aria-label={title}
      >
        <AsyncImage src={image} alt={title} staticSized height={172} />
      </div>
      {publisherLogo ? (
        <PublisherLogo
          publisherLogo={publisherLogo ?? null}
          publisherName={publisherName ?? ''}
          darkPublisherLogo={darkPublisherLogo}
          isDark={isDark}
          storySize={StorySize.MEDIUM}
        />
      ) : (
        ''
      )}
      {title ? (
        <h2 className="recirculation-card-title title-sm">{title}</h2>
      ) : (
        ''
      )}
      {category ? (
        <div className="recirculation-card-category label-sm">{category}</div>
      ) : (
        ''
      )}
    </>
  );

  const handleLinkClick = () => {
    if (uid && link) {
      const isLinkExternal = getIsLinkExternal(adjustFotoscapesLink(link));
      analytics.emitContentClickedEvent({
        source: 'recirculation',
        sourceSchedule: 'recirculation',
        partnerId: context.partner || '',
        contentId: uid || '',
        type: isLinkExternal ? 'external' : 'internal',
        originUrl: window.location.href,
        destinationUrl: link || '',
        title,
        contentOwner: owner || '',
        primaryInterest: category,
        language: lang,
        searchParams,
        numInteractions: getCurrentNumberOfInteractions(),
      });
    }
  };

  return (
    <div className="recirculation-card" data-testid="test-recirculation-card">
      {link ? (
        <FotoscapeLink
          to={adjustFotoscapesLink(link)}
          className="recirculation-card-link"
          onClick={handleLinkClick}
        >
          {cardContent}
        </FotoscapeLink>
      ) : (
        cardContent
      )}
    </div>
  );
};

export default RecirculationCard;
